import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import type {
  CompanyCreate,
  CompanyDetail,
  CompanyWrite,
} from "../../lib/crm-types";
import { Button } from "../ui/Button";
import { Overlay } from "../ui/Overlay";
import { CompanyAutofill } from "./CompanyAutofill";
import { ExistingCompanies } from "./ExistingCompanies";
import { Field, Input, Picker } from "./fields";

/**
 * Create or edit a company.
 *
 * **Creating asks for four things**: the name, the website, the region, and
 * the person you are talking to. That is what somebody has in front of them
 * when an account starts -- usually a phone call -- and everything else on the
 * record is either looked up later or typed in once somebody knows it. The
 * long form was being filled in top to bottom and abandoned, or filled in
 * with guesses, and the contact was a second form on a second page that was
 * skipped. The person goes in the same request, so the company never exists
 * without them unless nobody was named.
 *
 * City, the main line, where the lead came from and its priority are
 * offered beside the four, none of them required. The desk asked for them because they are
 * often known on that first call -- "how did you hear about us" is asked on
 * it -- and a question nobody asks again is lost if there is nowhere to put
 * the answer.
 *
 * **Editing is the full record.** The critical fields are one group and the
 * classification another, in that order, because the first group is what
 * makes the record findable and the second is what makes it reportable.
 * Company number and VAT number are explicitly labelled optional: around a
 * fifth of a trade desk's accounts are sole traders with neither.
 *
 * The register lookup (`CompanyAutofill`) stays on the create form even so.
 * It asks nothing -- it appears only when the register has an answer -- and
 * what it fills (company number, VAT, city, classification) is filled only
 * when somebody picks a company or a chip, so the hidden fields still hold
 * nothing that was not chosen. It is not on the edit form because there it
 * would list the company itself on every open, and picking a candidate
 * overwrites what is already set.
 */
export function CompanyDialog({
  company,
  onClose,
}: {
  /** Absent to create. */
  company?: CompanyDetail;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const editing = company !== undefined;

  /*
   * `CompanyWrite` plus the id box, which is held as a string. The write shape
   * wants `{ source, id }` or null, and a half-typed pair is neither -- so the
   * form keeps the text and builds the link on submit.
   */
  const [form, setForm] = useState<CompanyWrite & { externalId: string }>({
    name: company?.name ?? "",
    domain: company?.domain ?? "",
    email: company?.email ?? "",
    phone: company?.phone ?? "",
    tradeAccountNumber: company?.tradeAccountNumber ?? "",
    externalId: company?.external?.id ?? "",
    companyNumber: company?.companyNumber ?? "",
    vatNumber: company?.vatNumber ?? "",
    city: company?.city ?? "",
    region: company?.region ?? "",
    customerType: company?.customerType ?? "",
    lifecycleStage: company?.lifecycleStage ?? "",
    leadPriority: company?.leadPriority ?? "",
    leadSource: company?.leadSource ?? "",
    parentCompanyId: company?.parentCompanyId ?? null,
  });

  /* The first person at a new company. One name box rather than first and
     last: `contacts.name` is a single column, and splitting on the way in
     would mean guessing where "Mary Ann de la Cruz" divides. */
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const contactNamedWithoutEmail =
    !editing && contactName.trim() !== "" && contactEmail.trim() === "";

  const filters = useQuery({
    queryKey: queryKeys.crmFilters,
    queryFn: crmApi.filters,
    staleTime: 5 * 60_000,
  });

  const save = useMutation({
    mutationFn: async () => {
      const payload: CompanyWrite = {
        ...form,
        name: form.name.trim(),
        // Empty strings mean "cleared", which the API reads as null. Sending
        // "" would store an empty string and break every "is it set?" check.
        domain: form.domain?.trim() || null,
        email: form.email?.trim() || null,
        phone: form.phone?.trim() || null,
        tradeAccountNumber: form.tradeAccountNumber?.trim() || null,
        companyNumber: form.companyNumber?.trim() || null,
        vatNumber: form.vatNumber?.trim() || null,
        city: form.city?.trim() || null,
        region: form.region?.trim() || null,
        customerType: form.customerType || null,
        lifecycleStage: form.lifecycleStage || null,
        leadPriority: form.leadPriority || null,
        leadSource: form.leadSource || null,
        /* Both halves or neither. The box holds only the id, because this
           dialog is about the fulfilment platform -- a second source would be
           a second field, not a free-text one to mistype. */
        external: form.externalId.trim()
          ? { source: "team", id: form.externalId.trim() }
          : null,
      };

      if (editing) return crmApi.updateCompany(company.id, payload);

      const email = contactEmail.trim();
      const create: CompanyCreate = {
        ...payload,
        primaryContact: email
          ? { email, name: contactName.trim() || null }
          : null,
      };
      return crmApi.createCompany(create);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.crmCompaniesRoot,
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.crmCompany(saved.id),
      });
      onClose();
      if (!editing) navigate(`/crm/companies/${saved.id}`);
    },
  });

  const picklists = filters.data?.picklists ?? {};
  const field = (key: keyof CompanyWrite) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    /* The card was capped at `max-h-[80vh]`, which on iOS is 80% of the
       viewport *with the toolbars hidden* -- so the last of it, footer
       included, sat under Safari's own chrome. The frame measures in `dvh`
       and pins the actions; see `Overlay`. */
    <Overlay
      title={editing ? `Edit ${company.name}` : "New company"}
      onClose={onClose}
      footer={
        <>
          {save.isError ? (
            <p className="mr-auto text-xs text-sla-breached">
              {save.error instanceof ApiError
                ? save.error.message
                : "Could not save that company."}
            </p>
          ) : null}
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            size="sm"
            disabled={
              save.isPending ||
              form.name.trim() === "" ||
              contactNamedWithoutEmail ||
              (!editing && !form.region?.trim())
            }
          >
            {save.isPending ? "Saving…" : editing ? "Save" : "Create company"}
          </Button>
        </>
      }
    >
      <form
        id={FORM_ID}
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Company name" required className="sm:col-span-2">
            <Input
              value={form.name}
              onChange={field("name")}
              required
              autoFocus
            />
          </Field>

          <Field
            label="Website domain"
            hint="cef.co.uk — paste a URL if easier"
          >
            <Input value={form.domain ?? ""} onChange={field("domain")} />
          </Field>

          {/*
            Required here and nowhere else. The API still accepts a company
            with no region, because a record pushed in from elsewhere may not
            know one -- but somebody creating an account by hand has usually
            just spoken to them, and it is the moment the answer is cheapest to
            get. Editing keeps it beside City and does not insist, so an older
            record without one can still be saved.
          */}
          {!editing ? (
            <Field label="Region" required>
              <Input
                value={form.region ?? ""}
                onChange={field("region")}
                required
              />
            </Field>
          ) : null}

          {!editing ? (
            <>
              <Field label="City">
                <Input value={form.city ?? ""} onChange={field("city")} />
              </Field>
              <Field label="Main line">
                <Input
                  type="tel"
                  value={form.phone ?? ""}
                  onChange={field("phone")}
                />
              </Field>
              <Field label="Lead source">
                <Picker
                  value={form.leadSource ?? ""}
                  onChange={field("leadSource")}
                  options={picklists.lead_source ?? []}
                />
              </Field>
              <Field label="Lead priority">
                <Picker
                  value={form.leadPriority ?? ""}
                  onChange={field("leadPriority")}
                  options={picklists.lead_priority ?? []}
                />
              </Field>
            </>
          ) : null}

          {/*
            Who we already hold on this domain or under this name, looked up as
            it is typed. Duplicate prevention beats duplicate detection: the
            queue catches a mistake after somebody has to unpick it, this
            catches it while the person still has the account in front of them.
          */}
          {!editing ? (
            <ExistingCompanies
              domain={form.domain ?? ""}
              name={form.name}
              parentCompanyId={form.parentCompanyId ?? null}
              onAttach={(parentId, defaults) =>
                setForm((current) => ({
                  ...current,
                  parentCompanyId: parentId,
                  // Inherit the group's identity, but never its trade account:
                  // that is the thing that makes a branch its own account.
                  domain: defaults.domain ?? current.domain,
                  vatNumber: defaults.vatNumber ?? current.vatNumber,
                  companyNumber:
                    defaults.companyNumber ?? current.companyNumber,
                  customerType: defaults.customerType ?? current.customerType,
                  lifecycleStage:
                    defaults.lifecycleStage ?? current.lifecycleStage,
                  leadSource: defaults.leadSource ?? current.leadSource,
                }))
              }
              onDetach={() =>
                setForm((current) => ({ ...current, parentCompanyId: null }))
              }
            />
          ) : null}

          {/*
            What the register says the company *is*, found from the name or
            the domain. Separate from the duplicate check above: that one asks
            whether to create this at all, this one fills it in once the answer
            is yes. Create only -- see the header.
          */}
          {!editing ? (
            <CompanyAutofill
              domain={form.domain ?? ""}
              name={form.name}
              form={form}
              onApply={(patch) =>
                setForm((current) => ({ ...current, ...patch }))
              }
            />
          ) : null}

          {!editing ? (
            <>
              <p className="mt-1 text-xs font-medium uppercase tracking-wide text-muted sm:col-span-2">
                Primary contact
              </p>
              <Field label="Name">
                <Input value={contactName} onChange={setContactName} />
              </Field>
              <Field
                label="Email"
                hint={
                  contactNamedWithoutEmail
                    ? "Needed to add them as a contact"
                    : undefined
                }
              >
                <Input
                  type="email"
                  value={contactEmail}
                  onChange={setContactEmail}
                />
              </Field>
            </>
          ) : (
            <>
              {/* The switchboard, beside the general email for the same reason it
                  exists: both are how you reach the company rather than a person,
                  and the sales desk needs a number to ring when nobody is named. */}
              <Field label="Main line">
                <Input
                  type="tel"
                  value={form.phone ?? ""}
                  onChange={field("phone")}
                />
              </Field>

              <Field label="General email">
                <Input
                  type="email"
                  value={form.email ?? ""}
                  onChange={field("email")}
                />
              </Field>

              <Field label="Trade account number">
                <Input
                  value={form.tradeAccountNumber ?? ""}
                  onChange={field("tradeAccountNumber")}
                />
              </Field>
              {/*
                Edit only, like everything in this branch. Nobody has the
                fulfilment platform's id to hand at the moment they are typing a
                new company in; linking is its own job, done from the queue of
                companies that have no link yet.
              */}
              <Field
                label="Team ID"
                hint="Their id in the fulfilment platform, if you know it"
              >
                <Input
                  value={form.externalId}
                  onChange={(value) =>
                    setForm((current) => ({ ...current, externalId: value }))
                  }
                />
              </Field>

              <Field label="Company number" hint="Leave blank for a sole trader">
                <Input
                  value={form.companyNumber ?? ""}
                  onChange={field("companyNumber")}
                />
              </Field>
              <Field label="VAT number" hint="Leave blank for a sole trader">
                <Input value={form.vatNumber ?? ""} onChange={field("vatNumber")} />
              </Field>

              <Field label="City">
                <Input value={form.city ?? ""} onChange={field("city")} />
              </Field>
              <Field label="Region">
                <Input value={form.region ?? ""} onChange={field("region")} />
              </Field>

              <Field label="Customer type">
                <Picker
                  value={form.customerType ?? ""}
                  onChange={field("customerType")}
                  options={picklists.customer_type ?? []}
                />
              </Field>
              <Field label="Lifecycle stage">
                <Picker
                  value={form.lifecycleStage ?? ""}
                  onChange={field("lifecycleStage")}
                  options={picklists.lifecycle_stage ?? []}
                />
              </Field>
              <Field label="Lead priority">
                <Picker
                  value={form.leadPriority ?? ""}
                  onChange={field("leadPriority")}
                  options={picklists.lead_priority ?? []}
                />
              </Field>
              <Field label="Lead source">
                <Picker
                  value={form.leadSource ?? ""}
                  onChange={field("leadSource")}
                  options={picklists.lead_source ?? []}
                />
              </Field>
            </>
          )}
        </div>
      </form>
    </Overlay>
  );
}

/** Ties the footer's submit button to the form it now sits outside of. */
const FORM_ID = "company-form";
